// Invoice Number Service for Offline Sales
// Per-store sequence for this device. Public id stays offline-{pc}-{n} (no StoreDB in string).
// Local outbox uniqueness is (sale_id, store_db) — see outbox / storage schema.
import { hardwareBridge } from './hardwareBridge';
import axios from 'axios';
import { getStorage } from './storageAdapter';
import { Capacitor } from '@capacitor/core';
import { Device } from './Device';

const COUNTER_KEY_PREFIX = 'offline_invoice_counter';

const UPSERT_COUNTER_SQL = `INSERT INTO invoice_numbers (key, value, updated_at)
              VALUES (?, ?, CURRENT_TIMESTAMP)
              ON CONFLICT(key) DO UPDATE SET value = ?, updated_at = CURRENT_TIMESTAMP`;

/**
 * Coerce storage values to a non-negative integer counter.
 * @param {*} value
 * @returns {number}
 */
function toCounterInt(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.floor(n);
}

/**
 * Counter key for one store on this device.
 * @param {string} storeDb
 * @returns {string}
 */
function counterKeyForStore(storeDb) {
    const db = String(storeDb || '').trim();
    if (!db) {
        throw new Error('[InvoiceNumber] storeDb is required for offline invoice sequence');
    }
    return `${COUNTER_KEY_PREFIX}:${db}`;
}

/**
 * Get PC name for invoice ID generation
 * @returns {Promise<String>} - PC name
 */
async function getPCName() {
    try {
        if (window?.electronBridge && typeof window.electronBridge.getPcName === 'function') {
            return await window.electronBridge.getPcName();
        }

        if (window?.hardwareIPC || window?.electronBridge) {
            const data = await hardwareBridge.getPcData();
            return data?.hostname || 'UNKNOWN';
        }

        if (Capacitor.isNativePlatform()) {
            return await Device.getPCName(true);
        }

        const response = await axios.get(hardwareApi + 'utils/getPcData');
        return response.data?.hostname || 'UNKNOWN';
    } catch (error) {
        console.error('[InvoiceNumber] Failed to get PC name:', error);
        return 'UNKNOWN';
    }
}

/**
 * Highest trailing n in outbox sale_ids for THIS store only.
 * @param {Object} storage
 * @param {string} storeDb
 * @returns {Promise<number>}
 */
async function getMaxOutboxSequenceForStore(storage, storeDb) {
    const db = String(storeDb || '').trim();
    if (!db) return 0;

    try {
        const rows = await storage.query(
            `SELECT sale_id FROM outbox_sales WHERE store_db = ?`,
            [db]
        );
        if (!Array.isArray(rows) || rows.length === 0) return 0;

        let maxSeq = 0;
        for (const row of rows) {
            const saleId = String(row?.sale_id || '');
            const match = saleId.match(/-(\d+)$/);
            if (!match) continue;
            const seq = toCounterInt(match[1]);
            if (seq > maxSeq) maxSeq = seq;
        }
        return maxSeq;
    } catch (error) {
        console.warn('[InvoiceNumber] Failed to scan outbox sequences for store:', error);
        return 0;
    }
}

/**
 * @param {Object} storage
 * @param {string} storeDb
 * @returns {Promise<number>}
 */
async function readStoredCounter(storage, storeDb) {
    const key = counterKeyForStore(storeDb);
    const row = await storage.get(
        `SELECT value FROM invoice_numbers WHERE key = ?`,
        [key]
    );
    return row ? toCounterInt(row.value) : 0;
}

/**
 * @param {Object} storage
 * @param {string} storeDb
 * @param {number} value
 */
async function writeCounter(storage, storeDb, value) {
    const key = counterKeyForStore(storeDb);
    const next = toCounterInt(value);
    await storage.run(UPSERT_COUNTER_SQL, [key, next, next]);
}

/**
 * Next offline invoice number for this store on this device.
 * Format: offline-{pc}-{n} (store only in counter key / outbox store_db column).
 * @param {string} storeDb
 * @returns {Promise<String>}
 */
export async function getNextInvoiceNumber(storeDb) {
    try {
        const storage = await getStorage();
        const db = String(storeDb || '').trim();
        if (!db) {
            console.error('[InvoiceNumber] getNextInvoiceNumber requires storeDb');
            return `offline-${Date.now()}`;
        }

        const [stored, storeOutboxMax, pcName] = await Promise.all([
            readStoredCounter(storage, db),
            getMaxOutboxSequenceForStore(storage, db),
            getPCName()
        ]);

        // Only this store's counter + this store's pending outbox (not other stores)
        const next = Math.max(stored, storeOutboxMax) + 1;
        await writeCounter(storage, db, next);

        return `offline-${pcName}-${next}`;
    } catch (error) {
        console.error('[InvoiceNumber] Failed to get next invoice number:', error);
        return `offline-${Date.now()}`;
    }
}

/**
 * @param {string} storeDb
 * @returns {Promise<Number>}
 */
export async function getCurrentCounter(storeDb) {
    try {
        const storage = await getStorage();
        return await readStoredCounter(storage, storeDb);
    } catch (error) {
        console.error('[InvoiceNumber] Failed to get current counter:', error);
        return 0;
    }
}

/**
 * Apply this store's server sequence for this device.
 * Sets counter to max(serverSeq, this store's local pending outbox max) so unsynced
 * sales for THIS store are not reused. Never uses other stores' outbox.
 * @param {Number} value - latestSequence from server for this store+device
 * @param {string} storeDb
 * @returns {Promise<number>}
 */
export async function resetCounter(value = 0, storeDb) {
    try {
        const db = String(storeDb || '').trim();
        if (!db) {
            console.error('[InvoiceNumber] resetCounter requires storeDb');
            return toCounterInt(value);
        }

        const storage = await getStorage();
        const serverFloor = toCounterInt(value);
        const storeOutboxMax = await getMaxOutboxSequenceForStore(storage, db);
        // Replace with this store's server seq; only raise for THIS store's pending locals
        const next = Math.max(serverFloor, storeOutboxMax);
        await writeCounter(storage, db, next);

        if (next !== serverFloor) {
            console.log(
                `[InvoiceNumber] Store ${db}: server=${serverFloor}, storeOutboxMax=${storeOutboxMax} → ${next}`
            );
        }
        return next;
    } catch (error) {
        console.error('[InvoiceNumber] Failed to reset counter:', error);
        return toCounterInt(value);
    }
}

export default {
    getNextInvoiceNumber,
    getCurrentCounter,
    resetCounter
};
